/**
 * Find and replace over the labels the drawer lists: what each row shows now
 * (its shown label, or its raw label) with every match replaced, as a plan
 * the drawer previews and then writes in one `setShownLabels`.
 */
import { LabelChange, refusedShownLabel } from "../../model/shownLabels"
import { ItemRow, LabelRow, takenNames, VariableRow, wordsOf } from "./labelRows"

export interface ReplaceRequest {
  find: string
  replace: string
  ignoreCase: boolean
  /** A JavaScript regular expression, `$1` in the replacement; else plain text, the replacement literal. */
  pattern: boolean
  /** "all": variables and their items; "variables"; "items": categories, rows, cells and iterations. */
  scope: "all" | "variables" | "items"
}

export interface ReplaceOutcome {
  error: string | null
  /** `now`: what the row shows; `after`: what it will show (its raw label for a reset). */
  changes: { row: LabelRow; now: string; after: string; change: LabelChange }[]
  refused: { row: LabelRow; after: string; reason: string }[]
}

export const BAD_PATTERN = "This pattern cannot be read."
export const EMPTY_RESULT = "The label would be empty."

/**
 * How long a replace may take, and the clock it is measured with. A pattern
 * that backtracks badly can take seconds per label; the plan then stops
 * between two labels (one label's own match cannot be stopped).
 */
export interface ReplaceBudget {
  ms: number
  now: () => number
}
export const REPLACE_BUDGET: ReplaceBudget = { ms: 2000, now: () => performance.now() }

const count = (n: number): string => n.toLocaleString("en-US")

/** Why a replace stopped: its time ran out after `checked` of `total` labels. */
export const tooLong = (budget: ReplaceBudget, checked: number, total: number): string =>
  `The replace stopped after ${count(budget.ms / 1000)} seconds, with ${count(checked)} of ${count(total)} labels checked. Try a simpler pattern.`

const escapePattern = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const inScope = (row: LabelRow, scope: ReplaceRequest["scope"]): row is VariableRow | ItemRow =>
  row.kind === "variable"
    ? scope !== "items"
    : row.kind === "item"
      ? scope !== "variables"
      : false

/**
 * The plan of `request` over `listed` (the rows the drawer lists; gone rows
 * and headings are never touched). The text replaced is what the row shows
 * now: its shown label, else its raw label (for a nested variable of the
 * data, its words, `wordsOf`, never its path with "//"). A row whose text
 * does not change is skipped; an empty result is refused; a result equal to
 * the raw label is a reset (`shown: null`); any other result is checked as a
 * typed one (`refusedShownLabel`). Rows are taken in list order, and an
 * item's result is checked against the names the other items of its variable
 * and word (from `all`) keep or show after the changes planned before it, so
 * a result that clashes with a name already taken is refused. When `budget`
 * runs out the plan stops with `tooLong` as its error and changes nothing.
 */
export const planReplace = (
  listed: LabelRow[],
  all: LabelRow[],
  request: ReplaceRequest,
  budget: ReplaceBudget = REPLACE_BUDGET
): ReplaceOutcome => {
  let re: RegExp
  try {
    re = new RegExp(
      request.pattern ? request.find : escapePattern(request.find),
      request.ignoreCase ? "gi" : "g"
    )
  } catch {
    return { error: BAD_PATTERN, changes: [], refused: [] }
  }
  // Plain text: a function replacer, so "$" in the replacement stays as it is.
  const replaceIn = (text: string): string =>
    request.pattern ? text.replace(re, request.replace) : text.replace(re, () => request.replace)

  // The owner of an item in a clash sentence: its variable as shown now.
  const owners = new Map<string, string>()
  all.forEach((r) => {
    if (r.kind === "variable") owners.set(r.target.variable, r.shown ?? r.raw)
  })
  const taken = takenNames(all)
  const changes: ReplaceOutcome["changes"] = []
  const refused: ReplaceOutcome["refused"] = []

  const scoped = listed.filter((row): row is VariableRow | ItemRow => inScope(row, request.scope))
  const start = budget.now()
  for (const [checked, row] of scoped.entries()) {
    if (budget.now() - start > budget.ms)
      return { error: tooLong(budget, checked, scoped.length), changes: [], refused: [] }
    const now = row.shown ?? (row.kind === "variable" ? wordsOf(row) : row.raw)
    const after = replaceIn(now)
    if (after === now) continue
    const text = after.trim()
    if (!text) {
      refused.push({ row, after, reason: EMPTY_RESULT })
      continue
    }
    // Only the spaces around it changed: the label shows as it did.
    if (text === now) continue
    if (text === row.raw) {
      // A reset, unless the row has no shown label to reset.
      if (row.shown === null) continue
      changes.push({ row, now, after: row.raw, change: { target: row.target, shown: null } })
      if (row.kind === "item") taken.show(row, row.raw)
      continue
    }
    const reason =
      row.kind === "item"
        ? refusedShownLabel(
            text,
            [taken.clash(row, text)].filter((n): n is string => n !== null),
            { word: row.word, owner: owners.get(row.variable) ?? row.variable }
          )
        : refusedShownLabel(text, [], null)
    if (reason) {
      refused.push({ row, after: text, reason })
      continue
    }
    changes.push({ row, now, after: text, change: { target: row.target, shown: text } })
    if (row.kind === "item") taken.show(row, text)
  }
  return { error: null, changes, refused }
}
